import * as React from "react";
import { Check } from "lucide-react";

import { SpecularButton } from "./ui/specular-button.js";
import { Button } from "./ui/button.js";
import { TodoDayCard, type CardView } from "./calendar/TodoDayCard.js";
import { LeaveCircles, LeaveDayDialog } from "./calendar/LeaveDayCircles.js";
import { CATEGORY_STYLE } from "./calendar/todoStyles.js";
import { cn } from "../lib/utils.js";
import {
  CATEGORY_LABEL, CATEGORY_ORDER, buildDayView, categoryOf, firstLetter, groupByDate, isoDate, placeCard, summarize, todayKey,
  todoDateKey, type CalendarTodo, type TodoCategory, type TodoDraft,
} from "../lib/calendarTodos.js";

interface LeaveRequest {
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  halfDay: boolean;
  status?: string;
  employee: { firstName: string; lastName: string };
}

interface Holiday {
  date: string;
  name: string;
  isOptional: boolean;
  isNational: boolean;
}

/** Everything the personal calendar needs to behave as a todo list. Omitted on the Team calendar. */
export interface TodoCalendarProps {
  items: CalendarTodo[];
  loading: boolean;
  loadError: boolean;
  onRetry: () => void;
  onToggle: (id: string, done: boolean) => Promise<void>;
  onSave: (draft: TodoDraft, id?: string) => Promise<CalendarTodo | null>;
  onDelete: (id: string) => Promise<CalendarTodo | null>;
  onRestore: (todo: CalendarTodo) => Promise<void>;
  onMove: (ids: string[], date: string) => Promise<Map<string, string>>;
  onUndoMove: (previous: Map<string, string>) => Promise<void>;
}

interface Props {
  year: number;
  month: number; // 0-11
  requests: LeaveRequest[];
  holidays: Holiday[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  // false for a single-person calendar, where every chip is already "you".
  showEmployeeName?: boolean;
  // Signed-in user's employee id: their own leave is drawn as a rounded square.
  selfId?: string;
  // The signed-in user's own todos -- only ever passed on a personal calendar,
  // never the Team view, so no one else's todos can appear here.
  todos?: TodoCalendarProps;
  // Opens that day's card (and the todo's detail view) once the todos have loaded.
  deepLink?: { date: string; todoId?: string } | null;
  onDeepLinkHandled?: () => void;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const FILTER_KEY = "hrms.leaveCalendar.todoFilters";
const CARD_W = 340;
const CARD_H = 420;
// Static z-index ladder so the first circle sits on top of the next.
const Z = ["z-[20]", "z-[19]", "z-[18]", "z-[17]", "z-[16]", "z-[15]"];

interface Filters { hiddenCategories: TodoCategory[]; hideHolidays: boolean; hideLeave: boolean }

function readFilters(): Filters {
  try {
    const raw = JSON.parse(localStorage.getItem(FILTER_KEY) ?? "{}") as Partial<Filters>;
    return {
      hiddenCategories: Array.isArray(raw.hiddenCategories) ? raw.hiddenCategories.filter(c => CATEGORY_ORDER.includes(c)) : [],
      hideHolidays: !!raw.hideHolidays,
      hideLeave: !!raw.hideLeave,
    };
  } catch { return { hiddenCategories: [], hideHolidays: false, hideLeave: false }; }
}

const monthPrefixOf = (y: number, m: number) => isoDate(y, m, 1).slice(0, 7);

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

const filterChip = "inline-flex h-7 items-center gap-1.5 rounded-full border border-surface-200 px-2.5 text-xs font-semibold text-surface-700 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 aria-pressed:border-transparent aria-[pressed=false]:opacity-45";
const pill = "rounded-full px-2 py-0.5 text-[11px] font-bold";

export function TeamLeaveCalendar({ year, month, requests, holidays, onPrevMonth, onNextMonth, onToday, showEmployeeName = true, selfId, todos, deepLink, onDeepLinkHandled }: Props) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const holidayByDate = new Map(holidays.map(h => [h.date.split("T")[0]!, h]));
  const todayStr = todayKey();

  const cells: Array<{ day: number } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d });
  while (cells.length % 7 !== 0) cells.push(null);
  const rows = cells.length / 7;

  const [filters, setFilters] = React.useState<Filters>(readFilters);
  const updateFilters = (f: Filters) => {
    setFilters(f);
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(f)); } catch { /* storage unavailable */ }
  };
  const hidden = React.useMemo(() => new Set(filters.hiddenCategories), [filters.hiddenCategories]);
  const todosOn = !!todos;
  const hideHolidays = todosOn && filters.hideHolidays;
  const hideLeave = todosOn && filters.hideLeave;

  const isMobile = useMediaQuery("(max-width: 639px)");
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const rowsRef = React.useRef<HTMLDivElement>(null);
  const cellRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const [cellW, setCellW] = React.useState(160);
  const compact = cellW < 100;

  // Group once per fetch; a cell's view-model is memoised off this map.
  const monthTodos = React.useMemo(() => {
    const prefix = isoDate(year, month, 1).slice(0, 7);
    return (todos?.items ?? []).filter(t => todoDateKey(t).startsWith(prefix));
  }, [todos?.items, year, month]);
  const visibleTodos = React.useMemo(() => (todos?.items ?? []).filter(t => !hidden.has(categoryOf(t.tone))), [todos?.items, hidden]);
  const byDate = React.useMemo(() => groupByDate(visibleTodos), [visibleTodos]);
  const allByDate = React.useMemo(() => groupByDate(todos?.items ?? []), [todos?.items]);
  const summary = React.useMemo(() => summarize(monthTodos.filter(t => !hidden.has(categoryOf(t.tone))), todayStr), [monthTodos, hidden, todayStr]);
  const categoryCounts = React.useMemo(() => {
    const c: Record<TodoCategory, number> = { important: 0, work: 0, reminder: 0, travel: 0, personal: 0 };
    for (const t of monthTodos) c[categoryOf(t.tone)]++;
    return c;
  }, [monthTodos]);

  const maxCircles = compact ? 3 : 5;
  const dayViews = React.useMemo(() => {
    const m = new Map<string, ReturnType<typeof buildDayView>>();
    for (const [k, list] of byDate) m.set(k, buildDayView(list, k, todayStr, maxCircles));
    return m;
  }, [byDate, todayStr, maxCircles]);

  // ── Floating card ──────────────────────────────────────────────────────────
  const [open, setOpen] = React.useState<{ dateKey: string; view: CardView } | null>(null);
  const [placement, setPlacement] = React.useState<ReturnType<typeof placeCard> | { left: number; top: number; side: "center"; arrowY: number } | undefined>();
  const openerRef = React.useRef<HTMLElement | null>(null);
  const [focusDate, setFocusDate] = React.useState<string>(() => (todayStr.startsWith(isoDate(year, month, 1).slice(0, 7)) ? todayStr : isoDate(year, month, 1)));
  const pendingFocus = React.useRef(false);

  // Team view: the day whose full leave list is open.
  const [leaveDay, setLeaveDay] = React.useState<string | null>(null);

  const [snack, setSnack] = React.useState<{ message: string; undo: () => Promise<void> } | null>(null);
  const [announce, setAnnounce] = React.useState("");
  React.useEffect(() => {
    if (!snack) return;
    const t = setTimeout(() => setSnack(null), 6000);
    return () => clearTimeout(t);
  }, [snack]);

  function openDay(dateKey: string, view: CardView, opener: HTMLElement | null) {
    openerRef.current = opener ?? cellRefs.current.get(dateKey) ?? null;
    setFocusDate(dateKey);
    setOpen({ dateKey, view });
  }

  function closeCard() {
    setOpen(null);
    const opener = openerRef.current;
    if (opener && opener.isConnected) opener.focus();
  }

  // Month changed under an open card: close it (but not for a deep link that just set the month).
  const deepLinkMonth = React.useRef(false);
  React.useEffect(() => { if (deepLinkMonth.current) { deepLinkMonth.current = false; return; } setOpen(null); }, [year, month]);

  React.useEffect(() => {
    if (!deepLink || !todos || !deepLink.date.startsWith(monthPrefixOf(year, month))) return;
    if (todos.loading) return;
    const found = deepLink.todoId ? todos.items.some(t => t.id === deepLink.todoId) : false;
    deepLinkMonth.current = true;
    openDay(deepLink.date, found && deepLink.todoId ? { kind: "detail", id: deepLink.todoId } : { kind: "list" }, null);
    onDeepLinkHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, todos?.loading, year, month]);

  React.useLayoutEffect(() => {
    if (!open || isMobile) { setPlacement(undefined); return; }
    const compute = () => {
      const wrap = wrapRef.current, rowsEl = rowsRef.current, cell = cellRefs.current.get(open.dateKey);
      if (!wrap || !rowsEl || !cell) return;
      const w = wrap.getBoundingClientRect(), r = rowsEl.getBoundingClientRect(), c = cell.getBoundingClientRect();
      const day = Number(open.dateKey.slice(8));
      const idx = firstDow + day - 1;
      const col = idx % 7, row = Math.floor(idx / 7);
      const headH = r.top - w.top;
      if (w.width < 800) {
        // Tablet: no room beside the day, so centre it over the grid.
        setPlacement({ left: Math.max(8, (w.width - CARD_W) / 2), top: Math.max(64, Math.min(headH + 8, w.height - CARD_H - 8)), side: "center", arrowY: 0 });
        return;
      }
      setPlacement(placeCard({ col, row, rows, cellW: c.width, cellH: c.height, headH, cardW: CARD_W, cardH: CARD_H }));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [open, isMobile, firstDow, rows]);

  React.useLayoutEffect(() => {
    const first = [...cellRefs.current.values()][0];
    if (first) setCellW(first.getBoundingClientRect().width);
    const ro = new ResizeObserver(() => { const f = [...cellRefs.current.values()][0]; if (f) setCellW(f.getBoundingClientRect().width); });
    if (rowsRef.current) ro.observe(rowsRef.current);
    return () => ro.disconnect();
  }, [year, month]);

  // Roving tabindex: move DOM focus once the target cell exists (it may be in the next month).
  React.useEffect(() => {
    if (!pendingFocus.current) return;
    const el = cellRefs.current.get(focusDate);
    if (el) { pendingFocus.current = false; el.focus(); }
  }, [focusDate, year, month]);

  const monthPrefix = isoDate(year, month, 1).slice(0, 7);
  const cellInMonth = focusDate.startsWith(monthPrefix) ? focusDate : isoDate(year, month, 1);

  function shiftDate(dateKey: string, deltaDays: number): string {
    const d = new Date(`${dateKey}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    return isoDate(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function moveFocus(target: string) {
    pendingFocus.current = true;
    setFocusDate(target);
    const t = new Date(`${target}T00:00:00`);
    if (t.getFullYear() * 12 + t.getMonth() < year * 12 + month) onPrevMonth();
    else if (t.getFullYear() * 12 + t.getMonth() > year * 12 + month) onNextMonth();
  }

  function onCellKeyDown(e: React.KeyboardEvent<HTMLElement>, dateKey: string, dow: number) {
    if (!todosOn || e.target !== e.currentTarget) return;
    const clampToMonth = (k: string) => (k.startsWith(monthPrefix) ? k : k < monthPrefix ? isoDate(year, month, 1) : isoDate(year, month, daysInMonth));
    switch (e.key) {
      case "ArrowLeft": e.preventDefault(); moveFocus(shiftDate(dateKey, -1)); break;
      case "ArrowRight": e.preventDefault(); moveFocus(shiftDate(dateKey, 1)); break;
      case "ArrowUp": e.preventDefault(); moveFocus(shiftDate(dateKey, -7)); break;
      case "ArrowDown": e.preventDefault(); moveFocus(shiftDate(dateKey, 7)); break;
      case "Home": e.preventDefault(); moveFocus(clampToMonth(shiftDate(dateKey, -dow))); break;
      case "End": e.preventDefault(); moveFocus(clampToMonth(shiftDate(dateKey, 6 - dow))); break;
      case "PageUp": case "PageDown": {
        e.preventDefault();
        const dir = e.key === "PageUp" ? -1 : 1;
        const day = Number(dateKey.slice(8));
        const next = new Date(year, month + dir, 1);
        const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
        pendingFocus.current = true;
        setFocusDate(isoDate(next.getFullYear(), next.getMonth(), Math.min(day, last)));
        if (dir < 0) onPrevMonth(); else onNextMonth();
        break;
      }
      case "Enter": case " ": e.preventDefault(); openDay(dateKey, { kind: "list" }, e.currentTarget); break;
      case "n": case "N": e.preventDefault(); if (dateKey >= todayStr) openDay(dateKey, { kind: "form" }, e.currentTarget); break;
      case "t": case "T": e.preventDefault(); pendingFocus.current = true; setFocusDate(todayStr); onToday(); break;
    }
  }

  function requestsOn(dateStr: string): LeaveRequest[] {
    return requests.filter(r => r.startDate.split("T")[0]! <= dateStr && r.endDate.split("T")[0]! >= dateStr);
  }

  const openDayTodos = open ? (dayViews.get(open.dateKey)?.sorted ?? []) : [];
  const openAll = open ? (allByDate.get(open.dateKey) ?? []) : [];
  const lookup = React.useCallback((id: string) => todos?.items.find(t => t.id === id), [todos?.items]);

  async function handleDelete(id: string) {
    if (!todos) return;
    const removed = await todos.onDelete(id);
    if (removed) {
      setSnack({ message: "Todo deleted", undo: () => todos.onRestore(removed) });
      setAnnounce("Todo deleted");
    }
  }

  async function handleMoveOverdue() {
    if (!todos || !open) return;
    const ids = openDayTodos.filter(t => !t.done).map(t => t.id);
    if (ids.length === 0) return;
    try {
      const prev = await todos.onMove(ids, todayStr);
      const msg = `${ids.length} todo${ids.length > 1 ? "s" : ""} moved to today`;
      setSnack({ message: msg, undo: () => todos.onUndoMove(prev) });
      setAnnounce(msg);
    } catch { /* the hook already rolled back and reported */ }
  }

  return (
    <div ref={wrapRef} className="relative overflow-hidden rounded-[var(--radius-lg)] border border-surface-200 bg-surface-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-200 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="text-sm font-bold">{MONTH_NAMES[month]} {year}</div>
          {todosOn && (
            <div className="flex items-center gap-1.5" aria-label="Month summary">
              <span className={cn(pill, "bg-[var(--color-info-bg)] text-[color:var(--color-info-fg)]")}>{summary.open} open</span>
              <span className={cn(pill, "bg-[var(--color-success-bg)] text-[color:var(--color-success-fg)]")}>{summary.done} done</span>
              {summary.overdue > 0 && <span className={cn(pill, "bg-[var(--color-error-bg)] text-[color:var(--color-error-fg)]")}>{summary.overdue} overdue</span>}
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <SpecularButton size="sm" onClick={onPrevMonth} aria-label="Previous month">←</SpecularButton>
          {/* Already on the current month: nothing to jump to. */}
          <SpecularButton size="sm" onClick={onToday} disabled={todayStr.startsWith(monthPrefixOf(year, month))}>Today</SpecularButton>
          <SpecularButton size="sm" onClick={onNextMonth} aria-label="Next month">→</SpecularButton>
        </div>
      </div>

      {todosOn && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-surface-200 px-4 py-2" role="group" aria-label="Calendar filters">
          {CATEGORY_ORDER.map(c => (
            <button
              key={c}
              type="button"
              aria-pressed={!hidden.has(c)}
              className={cn(filterChip, !hidden.has(c) && CATEGORY_STYLE[c].chip)}
              onClick={() => updateFilters({ ...filters, hiddenCategories: hidden.has(c) ? filters.hiddenCategories.filter(x => x !== c) : [...filters.hiddenCategories, c] })}
            >
              <i className={cn("size-[7px] rounded-full", CATEGORY_STYLE[c].dot)} />
              {CATEGORY_LABEL[c]} <span className="tabular-nums opacity-80">{categoryCounts[c]}</span>
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-surface-200" aria-hidden="true" />
          <button type="button" aria-pressed={!filters.hideHolidays} className={cn(filterChip, !filters.hideHolidays && "bg-[var(--color-warning-bg)] text-[color:var(--color-warning-fg)]")} onClick={() => updateFilters({ ...filters, hideHolidays: !filters.hideHolidays })}>Holidays</button>
          <button type="button" aria-pressed={!filters.hideLeave} className={cn(filterChip, !filters.hideLeave && "bg-surface-100")} onClick={() => updateFilters({ ...filters, hideLeave: !filters.hideLeave })}>Leave</button>
        </div>
      )}

      {todos?.loadError && (
        <div role="alert" className="flex items-center justify-between gap-2 border-b border-surface-200 bg-[var(--color-error-bg)] px-4 py-2 text-[13px] text-[color:var(--color-error-fg)]">
          Couldn't load your todos.
          <Button size="sm" variant="outline" onClick={todos.onRetry}>Retry</Button>
        </div>
      )}

      <div role="grid" aria-label={`${MONTH_NAMES[month]} ${year}`} aria-rowcount={rows + 1}>
        <div role="row" className="grid grid-cols-7">
          {DOW.map(d => (
            <div key={d} role="columnheader" className="border-b border-surface-200 px-1 py-1.5 text-center text-[10.5px] font-bold uppercase tracking-[0.5px] text-surface-400">
              {d}
            </div>
          ))}
        </div>
        <div ref={rowsRef}>
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} role="row" className="grid grid-cols-7">
              {cells.slice(r * 7, r * 7 + 7).map((cell, ci) => {
                if (!cell) {
                  return <div key={ci} role="gridcell" aria-hidden="true" className="min-h-[94px] border-b border-r border-surface-100 bg-surface-50" />;
                }
                const dateStr = isoDate(year, month, cell.day);
                const dow = ci;
                const isWeekend = dow === 0 || dow === 6;
                const holiday = hideHolidays ? undefined : holidayByDate.get(dateStr);
                const dayRequests = hideLeave ? [] : requestsOn(dateStr);
                const isToday = dateStr === todayStr;
                const view = dayViews.get(dateStr);
                const total = view?.total ?? 0;
                const allCount = allByDate.get(dateStr)?.length ?? 0;
                const isSelected = open?.dateKey === dateStr;
                const label = [
                  `${cell.day} ${MONTH_NAMES[month]}`,
                  todosOn ? `${total} todo${total === 1 ? "" : "s"}${total ? `, ${view!.doneCount} done` : ""}` : "",
                  holiday ? `holiday ${holiday.name}` : "",
                ].filter(Boolean).join(": ").replace(": holiday", ", holiday");

                return (
                  <div
                    key={ci}
                    role="gridcell"
                    ref={(el) => { if (el) cellRefs.current.set(dateStr, el); else cellRefs.current.delete(dateStr); }}
                    tabIndex={todosOn ? (dateStr === cellInMonth ? 0 : -1) : undefined}
                    aria-label={todosOn ? label : undefined}
                    aria-selected={todosOn ? isSelected : undefined}
                    aria-current={isToday ? "date" : undefined}
                    onFocus={todosOn ? () => setFocusDate(dateStr) : undefined}
                    onKeyDown={todosOn ? (e) => onCellKeyDown(e, dateStr, dow) : undefined}
                    onClick={todosOn ? (e) => openDay(dateStr, { kind: "list" }, e.currentTarget) : dayRequests.length > 0 ? () => setLeaveDay(dateStr) : undefined}
                    data-date={dateStr}
                    className={cn(
                      "relative border-b border-r border-surface-100 p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
                      todosOn ? "min-h-[94px] cursor-pointer" : cn("min-h-[84px]", dayRequests.length > 0 && "cursor-pointer"),
                      isWeekend ? "bg-surface-50 [--cb:var(--color-surface-50)]" : "bg-surface-0 [--cb:var(--color-surface-0)]",
                      isToday && "bg-brand-50 ring-2 ring-inset ring-brand-500 [--cb:var(--color-brand-50)]",
                      isSelected && "bg-surface-100 ring-2 ring-inset ring-brand-500 [--cb:var(--color-surface-100)]",
                    )}
                  >
                    {isToday ? (
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex size-5 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">{cell.day}</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-wide text-brand-700">Today</span>
                      </div>
                    ) : (
                      <div className="inline-block text-[11px] font-semibold text-surface-500">{cell.day}</div>
                    )}
                    {holiday && (
                      <div
                        className={cn(
                          "mt-[3px] inline-block rounded px-[5px] py-px text-[9.5px] font-bold",
                          holiday.isNational ? "bg-[var(--color-warning-bg)] text-[color:var(--color-warning-fg)]" : "bg-[var(--color-info-bg)] text-[color:var(--color-info-fg)]",
                        )}
                        title={`${holiday.isNational ? "National" : "Company"} holiday${holiday.isOptional ? " (optional)" : ""}`}
                      >
                        {holiday.name}{holiday.isOptional ? " · Optional" : ""}
                      </div>
                    )}
                    <LeaveCircles requests={dayRequests} max={isMobile ? 3 : maxCircles} compact={compact || isMobile} selfId={selfId} onOpen={() => setLeaveDay(dateStr)} />

                    {todosOn && todos!.loading && allCount === 0 && !isMobile && (
                      <div className="absolute bottom-2.5 left-2.5 flex" aria-hidden="true">
                        <span className="size-5 animate-pulse rounded-full bg-surface-200 shadow-[0_0_0_2px_var(--cb)]" />
                        <span className="-ml-2 size-5 animate-pulse rounded-full bg-surface-200 shadow-[0_0_0_2px_var(--cb)]" />
                      </div>
                    )}

                    {todosOn && view && total > 0 && (isMobile ? (
                      <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[11px] font-bold text-surface-600" aria-hidden="true">
                        <i className={cn("size-2 rounded-full", CATEGORY_STYLE[categoryOf((view.sorted.find(t => !t.done) ?? view.sorted[0]!).tone)].dot)} />
                        {view.openCount}
                      </span>
                    ) : (
                      <>
                        <div className="absolute bottom-2.5 left-2.5 flex items-center">
                          {view.circles.map((t, i) => (
                            <button
                              key={t.id}
                              type="button"
                              title={t.done ? `${t.title} (done)` : t.title}
                              aria-label={`${t.title}, ${CATEGORY_LABEL[categoryOf(t.tone)]}, ${t.done ? "done" : "open"}`}
                              onClick={(e) => { e.stopPropagation(); openDay(dateStr, { kind: "detail", id: t.id }, e.currentTarget); }}
                              className={cn(
                                "relative flex shrink-0 animate-td-dot items-center justify-center rounded-full p-0 text-[11px] font-bold leading-none shadow-[0_0_0_2px_var(--cb)] transition-transform duration-200 hover:z-30 hover:-translate-y-[3px] hover:scale-[1.12] focus-visible:z-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                                compact ? "size-5" : "size-6",
                                i > 0 && (compact ? "-ml-[9px]" : "-ml-2"),
                                Z[i],
                                CATEGORY_STYLE[categoryOf(t.tone)].circle,
                                t.done && "opacity-50",
                              )}
                            >
                              {t.done ? <Check size={12} strokeWidth={3} /> : firstLetter(t.title)}
                            </button>
                          ))}
                          {view.overflow > 0 && (
                            <button
                              type="button"
                              aria-label={`${view.overflow} more todos`}
                              onClick={(e) => { e.stopPropagation(); openDay(dateStr, { kind: "list" }, e.currentTarget); }}
                              className={cn(
                                "relative z-[14] flex shrink-0 items-center justify-center rounded-full bg-surface-300 p-0 text-[11px] font-bold leading-none text-surface-900 shadow-[0_0_0_2px_var(--cb)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                                compact ? "-ml-[9px] size-5" : "-ml-2 size-6",
                              )}
                            >
                              +{view.overflow}
                            </button>
                          )}
                        </div>
                        <span className={cn("absolute bottom-3 right-2.5 text-[11px] font-semibold", view.overdue > 0 ? "text-error" : "text-surface-500")}>
                          {view.overdue > 0 ? `${view.overdue} overdue` : `${view.doneCount}/${view.total}`}
                        </span>
                      </>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {todos && open && (
        <TodoDayCard
          dateKey={open.dateKey}
          isToday={open.dateKey === todayStr}
          isPast={open.dateKey < todayStr}
          todos={openDayTodos}
          hiddenByFilters={openAll.length - openDayTodos.length}
          lookup={lookup}
          initialView={open.view}
          sheet={isMobile}
          placement={placement}
          onClose={closeCard}
          onShowAll={() => updateFilters({ ...filters, hiddenCategories: [] })}
          onToggle={(id, done) => { void todos.onToggle(id, done).catch(() => {}); setAnnounce(done ? "Marked as done" : "Marked as open"); }}
          onSave={async (draft, id) => {
            const saved = await todos.onSave(draft, id);
            setAnnounce("Todo saved");
            return saved;
          }}
          onDelete={handleDelete}
          onMoveOverdue={() => void handleMoveOverdue()}
        />
      )}

      {snack && (
        <div role="status" className="absolute bottom-3 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-xl bg-surface-900 px-4 py-2.5 text-[13px] text-surface-0 shadow-xl">
          {snack.message}
          <button
            type="button"
            className="font-bold underline"
            onClick={() => { const undo = snack.undo; setSnack(null); void undo().catch(() => {}); }}
          >
            Undo
          </button>
        </div>
      )}
      <LeaveDayDialog dateKey={leaveDay} selfId={selfId} requests={leaveDay ? requestsOn(leaveDay) : []} onClose={() => setLeaveDay(null)} />
      <div className="sr-only" role="status" aria-live="polite">{announce}</div>
    </div>
  );
}
